/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { area as turfArea, intersect } from '@turf/turf';
import type {
  Feature,
  FeatureCollection,
  MultiPolygon,
  Polygon,
} from 'geojson';
import { dateUtils } from '@opengeoweb/shared';
import { getDefaultValues } from './types';
import type {
  Area,
  WarningEventBody,
  WarningEvent,
} from '../../../store/warningsForm/types';
import { PublicWarningStatus } from '../../../store/warningsForm/types';
import {
  classifyPolygonClip,
  getPolygonMaskOverlap,
  isPolygonFeature,
} from '../../DrawingToolForm/maskUtils';
import type { PolygonClipResult } from '../../DrawingToolForm/maskUtils';
import { WarningLevel } from '../../PublicWarningsForm/LevelField';

// Earliest validFrom and latest validUntil across all warnings
const getEventValidity = (
  warnings: WarningEventBody[],
): Pick<WarningEvent, 'validFrom' | 'validUntil'> =>
  warnings.reduce<Pick<WarningEvent, 'validFrom' | 'validUntil'>>(
    (validity, warning) => ({
      validFrom:
        !validity.validFrom ||
        dateUtils.isBefore(
          dateUtils.isoStringToDate(warning.validFrom)!,
          dateUtils.isoStringToDate(validity.validFrom)!,
        )
          ? warning.validFrom
          : validity.validFrom,
      validUntil:
        !validity.validUntil ||
        dateUtils.isAfter(
          dateUtils.isoStringToDate(warning.validUntil)!,
          dateUtils.isoStringToDate(validity.validUntil)!,
        )
          ? warning.validUntil
          : validity.validUntil,
    }),
    { validFrom: '', validUntil: '' },
  );

// Single source of truth for the fields each warning row inherits from the parent event
const getInheritedEventFields = (
  values: WarningEvent,
): Pick<WarningEventBody, 'incident' | 'phenomenon'> => ({
  incident: values.incident,
  phenomenon: values.phenomenon,
});

interface FormData extends WarningEvent {
  IS_DRAFT?: boolean | string;
}

export const buildSubmitValues = (values: FormData): WarningEvent => {
  const { IS_DRAFT, ...productToPost } = values;
  const inherited = getInheritedEventFields(productToPost);

  const eventToPost = {
    ...productToPost,
    ...getEventValidity(productToPost.warnings),
    warnings: productToPost.warnings.map((warning) => {
      const joinedWarning = {
        ...warning,
        ...inherited,
        domain: 'public',
      } as WarningEventBody;
      // Remove empty fields from all warnings, but always send `areas` (even
      // when empty) so the backend can clear areas that were removed
      return Object.keys(joinedWarning).reduce<WarningEventBody>(
        (warningDetail: WarningEventBody, fieldName: string) => {
          const key = fieldName as keyof WarningEventBody;
          const fieldValue = joinedWarning[key];
          if (key === 'areas' || fieldValue) {
            return {
              ...warningDetail,
              [key]: fieldValue,
            };
          }

          return warningDetail;
        },
        {} as WarningEventBody,
      );
    }),
  };

  // remove empty event fields
  return Object.keys(eventToPost).reduce<WarningEvent>(
    (event: WarningEvent, fieldName: string) => {
      const key = fieldName as keyof WarningEvent;
      const fieldValue = eventToPost[key];
      if (Array.isArray(fieldValue) && !fieldValue.length) {
        return event;
      }
      if (fieldValue) {
        return {
          ...event,
          [key]: fieldValue,
        };
      }

      return event;
    },
    {} as WarningEvent,
  );
};

const LEVEL_ORDER: string[] = [
  WarningLevel.extreme,
  WarningLevel.severe,
  WarningLevel.moderate,
];

// a missing or unparsable date sorts last in a descending comparison
const toTimestamp = (date: string): number => {
  const timestamp = dateUtils.unixFromISOString(date);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

// an unknown level sorts below the known ones
const toLevelRank = (level: string): number => {
  const rank = LEVEL_ORDER.indexOf(level);
  return rank === -1 ? LEVEL_ORDER.length : rank;
};

// There is no timestamp for the moment a warning was expired or withdrawn, validUntil is the
// closest available: for an expired warning it is usually close to the expiry moment
const compareInactiveWarnings = (
  warningA: WarningEventBody,
  warningB: WarningEventBody,
): number =>
  toTimestamp(warningB.validUntil) - toTimestamp(warningA.validUntil) ||
  toTimestamp(warningB.validFrom) - toTimestamp(warningA.validFrom) ||
  toLevelRank(warningA.level) - toLevelRank(warningB.level);

// Expired warnings are moved to the end, withdrawn ones all the way to the bottom, both most
// recent first. Active and not yet active warnings keep their relative order
export const sortWarningsByActivity = (
  warnings: WarningEventBody[],
): WarningEventBody[] => {
  const inactiveByStatus = (status: PublicWarningStatus): WarningEventBody[] =>
    warnings
      .filter((warning) => warning.status === status)
      .sort(compareInactiveWarnings);

  return [
    ...warnings.filter((warning) => !isWarningFinalised(warning)),
    ...inactiveByStatus(PublicWarningStatus.EXPIRED),
    ...inactiveByStatus(PublicWarningStatus.WITHDRAWN),
  ];
};

export const getFormData = (
  warningsEvent: WarningEvent | undefined,
  // object?: DrawingListItem,
): FormData => {
  const data = {
    IS_DRAFT: '',
    ...(warningsEvent
      ? {
          ...getDefaultValues(),
          ...warningsEvent,
        }
      : getDefaultValues()),
    // object,
  };

  return {
    ...data,
    warnings: sortWarningsByActivity(data.warnings),
  };
};

export const constructWarningWithNewStatus = (
  warningsEvent: WarningEvent,
  status: PublicWarningStatus,
): WarningEventBody[] => {
  return warningsEvent.warnings.map((warning: WarningEventBody) => {
    // Skip any warnings with status EXPIRED or WITHDRAWN
    if (isWarningFinalised(warning)) {
      return warning;
    }
    return {
      ...warning,
      status,
    } as WarningEventBody;
  });
};

export const hasWarningStarted = (
  warning: Pick<WarningEventBody, 'validFrom'>,
): boolean =>
  dateUtils.unixFromISOString(warning.validFrom) <=
  dateUtils.unix(dateUtils.getNowUtc());

export const isWarningFinalised = (
  warning: Pick<WarningEventBody, 'status'>,
): boolean =>
  warning.status === PublicWarningStatus.EXPIRED ||
  warning.status === PublicWarningStatus.WITHDRAWN;

export const getEventStatusActions = (
  warnings: WarningEventBody[],
): {
  canExpire: boolean;
  canWithdraw: boolean;
  hasPendingWarnings: boolean;
} => {
  const pending = warnings.filter((warning) => !isWarningFinalised(warning));
  const hasPendingWarnings = pending.length > 0;
  const canWithdraw =
    hasPendingWarnings &&
    !pending.some(hasWarningStarted) &&
    !warnings.some((warning) => warning.status === PublicWarningStatus.EXPIRED);
  const canExpire = hasPendingWarnings && !canWithdraw;

  return { canExpire, canWithdraw, hasPendingWarnings };
};

export const getWarningStatusesForEventAction = (
  warnings: WarningEventBody[],
  action: PublicWarningStatus.EXPIRED | PublicWarningStatus.WITHDRAWN,
): (PublicWarningStatus | undefined)[] =>
  warnings.map((warning) => {
    // Skip any warnings that are already finalised
    if (isWarningFinalised(warning)) {
      return undefined;
    }
    if (action === PublicWarningStatus.WITHDRAWN) {
      return PublicWarningStatus.WITHDRAWN;
    }
    return hasWarningStarted(warning)
      ? PublicWarningStatus.EXPIRED
      : PublicWarningStatus.WITHDRAWN;
  });

// Minimum intersection, as a fraction of the smaller area, to count as a real
// overlap. Areas sharing an edge can overlap by a thin sliver because drawing
// coordinates are rounded to 5 decimals; that should not count as overlap.
const MIN_OVERLAP_FRACTION = 0.001;

const doFeaturesOverlap = (
  featureA: Feature<Polygon | MultiPolygon>,
  featureB: Feature<Polygon | MultiPolygon>,
): boolean => {
  const pair: FeatureCollection<Polygon | MultiPolygon> = {
    type: 'FeatureCollection',
    features: [featureA, featureB],
  };
  const intersection = intersect(pair);
  if (!intersection) {
    return false;
  }
  const smallestArea = Math.min(turfArea(featureA), turfArea(featureB));
  return turfArea(intersection) > smallestArea * MIN_OVERLAP_FRACTION;
};

export const hasAreaData = (areas?: Area[]): boolean =>
  !!areas?.some((area) => !!area.geoJSON?.features?.length);

export const doAreasOverlap = (areasA?: Area[], areasB?: Area[]): boolean => {
  if (!hasAreaData(areasA) || !hasAreaData(areasB)) {
    return true;
  }

  return areasA!.some((areaA) =>
    areaA.geoJSON.features
      .filter(isPolygonFeature)
      .some((featureA) =>
        areasB!.some((areaB) =>
          areaB.geoJSON.features
            .filter(isPolygonFeature)
            .some((featureB) => doFeaturesOverlap(featureA, featureB)),
        ),
      ),
  );
};

export const doTimeRangesOverlap = (
  aFrom: string,
  aUntil: string,
  bFrom: string,
  bUntil: string,
): boolean =>
  dateUtils.unixFromISOString(aFrom) < dateUtils.unixFromISOString(bUntil) &&
  dateUtils.unixFromISOString(bFrom) < dateUtils.unixFromISOString(aUntil);

// Two warnings conflict when they share the same level AND overlap in BOTH
// time and area at the same time.
export const doWarningsOverlap = (
  warningA: WarningEventBody,
  warningB: WarningEventBody,
): boolean =>
  warningA.level === warningB.level &&
  doTimeRangesOverlap(
    warningA.validFrom,
    warningA.validUntil,
    warningB.validFrom,
    warningB.validUntil,
  ) &&
  doAreasOverlap(warningA.areas, warningB.areas);

export const getOverlappingWarnings = (
  warnings: WarningEventBody[],
  index: number,
): number[] => {
  const target = warnings[index];
  if (!target) {
    return [];
  }

  return warnings.reduce<number[]>((overlaps, warning, i) => {
    // Exclude withdrawn and expired warnings when checking for overlap
    if (
      warning.status !== PublicWarningStatus.EXPIRED &&
      warning.status !== PublicWarningStatus.WITHDRAWN &&
      i !== index &&
      doWarningsOverlap(target, warning)
    ) {
      overlaps.push(i);
    }
    return overlaps;
  }, []);
};

export const hasApprovedArea = (
  approvedArea?: FeatureCollection,
): approvedArea is FeatureCollection =>
  !!approvedArea?.features?.some(isPolygonFeature);

/**
 * Checks the polygons of the geoJSON against the approved area.
 * - inside: the geoJSON is returned unchanged
 * - clipped: polygons partly outside the approved area are clipped to it
 * - outside: at least one polygon lies completely outside the approved area,
 *   the geoJSON is returned unchanged
 * - error: at least one polygon could not be clipped, the geoJSON is returned unchanged
 * Without an approved area, the check is skipped and the result is inside.
 * Non polygon features are not checked.
 */
export const clipToApprovedArea = (
  geoJSON: FeatureCollection,
  approvedArea?: FeatureCollection,
): { geoJSON: FeatureCollection; result: PolygonClipResult } => {
  if (!hasApprovedArea(approvedArea)) {
    return { geoJSON, result: 'inside' };
  }
  const clippedFeatures = geoJSON.features.map((feature) =>
    isPolygonFeature(feature)
      ? classifyPolygonClip(feature, approvedArea)
      : { feature, result: 'inside' as const },
  );
  const results = clippedFeatures.map(({ result }) => result);
  if (results.includes('outside')) {
    return { geoJSON, result: 'outside' };
  }
  if (results.includes('error')) {
    return { geoJSON, result: 'error' };
  }
  if (results.includes('clipped')) {
    return {
      geoJSON: {
        ...geoJSON,
        features: clippedFeatures.map(({ feature }) => feature),
      },
      result: 'clipped',
    };
  }
  return { geoJSON, result: 'inside' };
};

export type ApprovedAreaError = 'outside' | 'error';

/**
 * Checks the polygons of the areas against the approved area:
 * - error: at least one polygon could not be checked against the approved area
 * - outside: at least one polygon lies completely outside the approved area
 * - undefined: all polygons (partly) lie within the approved area, or there is
 *   no approved area to check against
 */
export const getApprovedAreaError = (
  areas?: Area[],
  approvedArea?: FeatureCollection,
): ApprovedAreaError | undefined => {
  if (!hasApprovedArea(approvedArea)) {
    return undefined;
  }
  const overlaps = (areas ?? []).flatMap((area) =>
    (area.geoJSON?.features ?? [])
      .filter(isPolygonFeature)
      .map((feature) => getPolygonMaskOverlap(feature, approvedArea)),
  );
  if (overlaps.includes('error')) {
    return 'error';
  }
  if (overlaps.includes('outside')) {
    return 'outside';
  }
  return undefined;
};
